import React, { useCallback, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useLinearProgressCalc, useTypedTranslation } from '@app/hooks';
import { LinearProgress, Link } from '@material-ui/core';
import { SetLoginForm } from '@app/models';
import { goBackToList } from '@app/modules/mfaToken/helpers';
import { ErrorBlock, PasslyButton } from '@app/components';
import TimerOffIcon from '@material-ui/icons/TimerOff';
import Icon from '@app/components/Icon/index';

interface IProps {
    setLoginForm: SetLoginForm;
    cbAfterMount: Function;
    secondButton?: JSX.Element;
    isMultipleProviders: boolean;
    verifyUser?: boolean;
}

export const TwoFactorPush: React.SFC<IProps> = ({ setLoginForm, cbAfterMount, secondButton, isMultipleProviders, verifyUser = false }) => {
    const { translation } = useTypedTranslation();
    const completed = useLinearProgressCalc(180);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isPushTimeout, setIsPushTimeout] = useState<boolean>(false);
    const [resendPushQuery, setResendPushQuery] = useState<boolean>(false);
    const handleResendPush = useCallback(() => {
        setResendPushQuery((prevValue) => !prevValue);
    }, []);

    useEffect(() => {
        setErrorMessage('');

        // this is for handling test push and verify user with push
        cbAfterMount()
            .then((data: any) => {
                if (data !== undefined && ((data.Success !== undefined && !data.Success) || (data.success !== undefined && !data.success))) {
                    if (data.ErrorType === 'timeout') {
                        setErrorMessage(verifyUser ? translation('MFA_PUSH_ERROR_TIMEOUT_VERIFY_USER') : translation('MFA_PUSH_ERROR_TIMEOUT'));
                        setIsPushTimeout(true);
                    } else {
                        setErrorMessage(translation('MFA_PUSH_ERROR'));
                    }
                }
            })
            .catch((err: AxiosError<any>) => {
                // this is for handling login failures with push
                const { error_description, error } = err?.response?.data ?? {};

                if (error_description === 'push_timeout') {
                    setErrorMessage(verifyUser ? translation('MFA_PUSH_ERROR_TIMEOUT_VERIFY_USER') : translation('MFA_PUSH_ERROR_TIMEOUT'));
                    setIsPushTimeout(true);
                } else if (error === 'server_error') {
                    setErrorMessage(translation('MFA_PUSH_ERROR'));
                } else {
                    setErrorMessage(translation('MFA_PUSH_ERROR'));
                }
            });
    }, [resendPushQuery]);

    const isError = !!errorMessage;
    return (
        <div className='two-factor-push'>
            <h1>{translation('MFA_PUSH_PUSH_TITLE')}</h1>
            <p className='two-factor-push__text'>
                {translation('MFA_PUSH_PUSH_APPROVE')}
                {isMultipleProviders && <Link onClick={goBackToList(setLoginForm)}>{translation('MFA_VERIFY_ANOTHER_WAY')}</Link>}
            </p>
            <ErrorBlock className='two-factor-push__error' isError={isError} errorMessage={errorMessage} />
            <Icon icon='pushAuthBigIco' className='two-factor-push__icon' />
            {isError ? (
                <div>
                    {isPushTimeout && (
                        <div className='two-factor-push__icon--wrapper'>
                            <TimerOffIcon color='primary' className='two-factor-push__icon' />
                        </div>
                    )}
                    <PasslyButton
                        id='resendPushQuery'
                        color='primary'
                        variant='contained'
                        className='two-factor-push__error--button'
                        onClick={handleResendPush}
                    >
                        {translation('TRY_AGAIN')}
                    </PasslyButton>
                </div>
            ) : (
                <LinearProgress className='two-factor-push__linear-progress' variant='determinate' value={completed} />
            )}
            {secondButton}
        </div>
    );
};
